import type { CharacterActionDescriptor } from "../pipeline";

export interface DescribedItem {
  /** Left out of the event when the item has no id (Custom Background) */
  itemId?: number | string;
  name: string | undefined;
}

// Only include itemId when there is one: a key that's present but undefined
// is sent as ITEM_ID: null
const withItemId = ({ itemId }: DescribedItem) =>
  itemId === undefined ? {} : { itemId };

/**
 * Describes moving from one chosen item to another, for choices that hold a
 * single item (background, species, ability score method):
 * - nothing to an item: "select"
 * - an item to another: one "change", with the new item's id and name and
 *   the previous item's name in oldValue
 * - an item to nothing: "deselect", with the removed item's name in oldValue
 *
 * newValue is always the value after the change and oldValue the value
 * before it, so a deselect has no newValue.
 */
export const createCharacterActionDescriptor = (
  previous: DescribedItem | null,
  next: DescribedItem | null
): CharacterActionDescriptor[] => {
  if (previous && next) {
    return [
      {
        changeType: "change",
        ...withItemId(next),
        newValue: next.name,
        oldValue: previous.name,
      },
    ];
  }
  if (next) {
    return [{ changeType: "select", ...withItemId(next), newValue: next.name }];
  }
  if (previous) {
    return [
      {
        changeType: "deselect",
        ...withItemId(previous),
        oldValue: previous.name,
      },
    ];
  }
  return [];
};
